export interface AnimatedNumberParts {
    prefix: string;
    suffix: string;
    value: number;
    decimals: number;
    decimalSeparator: '.' | ',';
    groupSeparator: ',' | '.' | ' ' | '\u00a0' | '\u202f' | '';
}

export interface AnimatedTextPart {
    text: string;
    number?: AnimatedNumberParts;
}

// Only complete three-digit groups belong to a grouped number. Spaces and
// punctuation between separate numbers must remain authored text.
const NUMBER_TOKEN = /(?:\d{1,3}(?:[, .\u00a0\u202f]\d{3})+(?:[.,]\d+)?|\d+(?:[.,]\d+)?)/;

function getSeparators(token: string) {
    const compact = token.replace(/[\s\u00a0]/g, '');
    const commaIndex = compact.lastIndexOf(',');
    const dotIndex = compact.lastIndexOf('.');
    const spaceSeparator = token.match(/[ \u00a0\u202f]/)?.[0] as AnimatedNumberParts['groupSeparator'] | undefined;

    if (commaIndex >= 0 && dotIndex >= 0) {
        const decimalSeparator = commaIndex > dotIndex ? ',' : '.';
        return {
            decimalSeparator,
            groupSeparator: decimalSeparator === ',' ? '.' : ',',
        } as const;
    }

    let separator: ',' | '.' | '' = '';
    if (commaIndex >= 0) separator = ',';
    if (dotIndex >= 0) separator = '.';
    if (!separator) {
        return { decimalSeparator: '.' as const, groupSeparator: spaceSeparator || '' as const };
    }

    const chunks = compact.split(separator);
    const isGrouped = chunks.length > 2 || chunks.at(-1)?.length === 3;

    if (isGrouped) {
        return { decimalSeparator: '.' as const, groupSeparator: separator };
    }

    return { decimalSeparator: separator, groupSeparator: spaceSeparator || '' as const };
}

export function parseAnimatedNumber(text: string): AnimatedNumberParts | null {
    const match = NUMBER_TOKEN.exec(text);
    if (!match) return null;

    const token = match[0];
    const { decimalSeparator, groupSeparator } = getSeparators(token);
    const normalized = token
        .replace(/[\s\u00a0]/g, '')
        .replace(groupSeparator ? new RegExp(`\\${groupSeparator}`, 'g') : /$^/, '')
        .replace(decimalSeparator, '.');
    const value = Number(normalized);

    if (!Number.isFinite(value)) return null;

    const decimalIndex = token.lastIndexOf(decimalSeparator);
    const decimals = decimalIndex >= 0 && decimalSeparator !== groupSeparator
        ? token.length - decimalIndex - 1
        : 0;

    return {
        prefix: text.slice(0, match.index),
        suffix: text.slice(match.index + token.length),
        value,
        decimals,
        decimalSeparator,
        groupSeparator,
    };
}

/** Split text into independently measured numeric runs and stationary text. */
export function splitAnimatedText(text: string, firstNumberOnly = false): AnimatedTextPart[] {
    const parts: AnimatedTextPart[] = [];
    let position = 0;

    const matches = Array.from(text.matchAll(new RegExp(NUMBER_TOKEN.source, 'g')));
    (firstNumberOnly ? matches.slice(0, 1) : matches).forEach((match) => {
        if (match.index > position) parts.push({ text: text.slice(position, match.index) });
        const number = parseAnimatedNumber(match[0]);
        parts.push(number ? { text: match[0], number } : { text: match[0] });
        position = match.index + match[0].length;
    });

    if (position < text.length) parts.push({ text: text.slice(position) });
    return parts;
}

function groupInteger(value: string, separator: AnimatedNumberParts['groupSeparator']) {
    if (!separator) return value;
    const sign = value.startsWith('-') ? '-' : '';
    const digits = sign ? value.slice(1) : value;
    return `${sign}${digits.replace(/\B(?=(\d{3})+(?!\d))/g, separator)}`;
}

export function formatAnimatedValue(parts: AnimatedNumberParts, currentValue: number): string {
    const fixed = currentValue.toFixed(parts.decimals);
    const [integer, fraction] = fixed.split('.');
    const grouped = groupInteger(integer, parts.groupSeparator);
    return fraction === undefined
        ? grouped
        : `${grouped}${parts.decimalSeparator}${fraction}`;
}

export function formatAnimatedText(parts: AnimatedNumberParts, currentValue: number): string {
    return `${parts.prefix}${formatAnimatedValue(parts, currentValue)}${parts.suffix}`;
}
